// MJOS-147 — assistant-ui external-store runtime bound to the pi-agent-core Agent.
//
// STREAMING MODEL (matches pi's canonical StreamingMessageContainer):
//   pi keeps the committed transcript in `agent.state.messages` and the
//   in-flight assistant turn in `agent.state.streamingMessage` (mutated IN
//   PLACE, then cleared at message_end when the final lands in state.messages).
//   On EVERY agent event we compute the display list:
//       committed = agent.state.messages
//       partial   = agent.state.streamingMessage
//       next      = partial ? [...committed, structuredClone(partial)]
//                           : [...committed]
//   The partial is DEEP-CLONED because pi mutates it in place — React/assistant
//   -ui only re-render when nested content changes by identity. This is what
//   streams partial text into the bubble token-by-token. At message_end pi
//   clears the partial and the final is already in `committed`, so the partial
//   naturally drops with no double-render.
//
//   Running state is driven straight off pi: setIsRunning(state.isStreaming).
//   The partial is tagged with a constant id ("streaming-partial") and is the
//   only message marked status:"running"; committed messages are "complete".
//
// CONVERSION: toDisplayMessages folds pi toolResult messages into the matching
// tool-call part, then useExternalMessageConverter maps each enriched message
// → ThreadMessageLike with per-message memo caching (so a token re-render only
// re-converts the partial, not the whole transcript). Both helpers live in the
// pure, unit-testable convert-message.ts module.
import { useEffect, useRef, useState } from "react";
import {
	AssistantRuntimeProvider,
	useExternalMessageConverter,
	useExternalStoreRuntime,
	type AppendMessage,
} from "@assistant-ui/react";
import type { Agent, AgentMessage } from "@earendil-works/pi-agent-core";
import { createMyJarvisAgent, type GetAccessToken } from "./agent-runtime";
import {
	convertMessage,
	createDisplayCache,
	toDisplayMessages,
	type DisplayMessage,
} from "./convert-message";

// Constant id for the in-flight assistant partial — the converter uses it to
// mark exactly that message status:"running".
const STREAMING_PARTIAL_ID = "streaming-partial";

export function MyJarvisRuntimeProvider({
	getAccessToken,
	userEmail,
	children,
	createAgent,
}: {
	getAccessToken: GetAccessToken;
	userEmail?: string;
	children: React.ReactNode;
	// Optional injection seam (MJOS-147 E2E). Prod never passes this — the panel
	// falls back to the real createMyJarvisAgent below, so prod behavior is
	// identical. The test injects a controllable fake Agent (which also means
	// getAccessToken is never invoked → no auth/network needed in the harness).
	createAgent?: () => Promise<Agent>;
}) {
	// `displayMessages` is the decorated pi transcript (committed + cloned
	// partial), already tool-stitched + id/status-stamped. These objects feed
	// straight into the converter, which memoizes by their identity.
	const [displayMessages, setDisplayMessages] = useState<DisplayMessage[]>([]);
	const [isRunning, setIsRunning] = useState(false);
	const agentRef = useRef<Agent | null>(null);
	const creatingRef = useRef(false);
	// Last agent error we logged — gates console.error so a persisted error isn't
	// re-logged on every event. There is NO UI surface for errors: an abort is a
	// user action (not an error), and a top banner pushed the layout down + cut
	// off the composer.
	const lastErrorRef = useRef<string | null>(null);
	// One identity-preserving cache for the panel's lifetime (see convert-message).
	const displayCacheRef = useRef(createDisplayCache());

	useEffect(() => {
		if (agentRef.current || creatingRef.current) return;
		creatingRef.current = true;
		let unsubscribe: (() => void) | undefined;
		let cancelled = false;

		// Recompute the display list from pi state on every event. Committed turns
		// keep their identity (cache hit → no remount); the partial is deep-cloned
		// each tick so its growing text re-renders.
		const sync = (agent: Agent) => {
			const committed = agent.state.messages as AgentMessage[];
			const partial = agent.state.streamingMessage as AgentMessage | undefined;
			let raw: AgentMessage[];
			let runningId: string | undefined;
			if (partial) {
				const clone = structuredClone(partial) as unknown as Record<
					string,
					unknown
				>;
				// Stamp the constant id so toDisplayMessages marks ONLY this running.
				clone.id = STREAMING_PARTIAL_ID;
				raw = [...committed, clone as unknown as AgentMessage];
				runningId = STREAMING_PARTIAL_ID;
			} else {
				raw = [...committed];
			}
			setDisplayMessages(
				toDisplayMessages(raw, runningId, displayCacheRef.current),
			);
			// Drive running state straight from pi.
			setIsRunning(agent.state.isStreaming);
			// Log a real agent error to the console once (no UI banner). Skip abort
			// errors — aborting is a user action, not a failure.
			const err = agent.state.errorMessage ?? null;
			if (err && err !== lastErrorRef.current && !/abort/i.test(err)) {
				console.error("[agent-panel] agent error:", err);
			}
			lastErrorRef.current = err;
		};

		(async () => {
			try {
				const agent = await (createAgent
					? createAgent()
					: createMyJarvisAgent({ getAccessToken, userEmail }));
				if (cancelled) {
					// Lost the race (unmounted before creation resolved) — stop the
					// agent so a mid-flight run + its tool calls don't leak.
					agent.abort();
					return;
				}
				agentRef.current = agent;
				sync(agent); // seed with whatever is already in state (usually empty)
				// pi-agent-core has NO `state-update` event. The live transcript
				// arrives via message_start / message_update / message_end / turn_end
				// / tool_execution_* / agent_end, and pi mutates state IN PLACE. So we
				// resync on EVERY event. (A previous build keyed off a non-existent
				// `state-update` event, so nothing ever rendered → "not responding".)
				unsubscribe = agent.subscribe((event: { type?: string }) => {
					sync(agent);
					// pi considers the run idle only AFTER all `agent_end` listeners
					// settle and `finishRun()` clears `state.isStreaming` (pi agent.js:
					// "considered idle later, after ... finishRun() clears runtime-owned
					// state"). Reading isStreaming inside the agent_end handler still sees
					// `true`, and no further event fires — so the Stop button never
					// reverts to Send. Re-sync on a macrotask, once finishRun has run, to
					// read the now-false isStreaming. Mirrors pi's own AgentInterface
					// (deferred Lit render) + the prior setup.ts setTimeout(0) pattern.
					if (event?.type === "agent_end") setTimeout(() => sync(agent), 0);
				});
			} catch (err) {
				console.error("[MyJarvisRuntimeProvider] Failed to create agent:", err);
			} finally {
				creatingRef.current = false;
			}
		})();

		return () => {
			cancelled = true;
			if (unsubscribe) unsubscribe();
			// Abort any in-flight run on unmount so the agent loop + tool calls stop.
			agentRef.current?.abort();
		};
		// getAccessToken / userEmail are stable for the panel's lifetime; we
		// intentionally create the agent exactly once.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const onNew = async (message: AppendMessage) => {
		const text = message.content.find((c) => c.type === "text")?.text ?? "";
		const agent = agentRef.current;
		if (text && agent) {
			await agent.prompt(text);
		}
	};

	// Per-message memo conversion — a token re-render only re-converts the
	// partial (fresh identity), not the whole transcript (cached identities).
	const messages = useExternalMessageConverter<DisplayMessage>({
		callback: convertMessage,
		messages: displayMessages,
		isRunning,
	});

	const runtime = useExternalStoreRuntime({
		messages,
		isRunning,
		onNew,
		// pi exposes Agent.abort() — stops the loop + any in-flight tool call.
		onCancel: async () => {
			agentRef.current?.abort();
		},
	});

	return (
		<AssistantRuntimeProvider runtime={runtime}>
			{children}
		</AssistantRuntimeProvider>
	);
}
